import { normalizeIcd10Key } from '@seen/core/models/organizationConditionsList'
import {
  ICD10_RAF_WEIGHTS_MAX_CODES,
  type Icd10RafWeight,
} from '@seen/request-types/icd10RafWeights'
import {
  type DefaultError,
  isCancelledError,
  useQueryClient,
} from '@tanstack/react-query'
import { chunk } from 'lodash-es'
import { useContext, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { SeenApiContext } from '#src/contexts/seen-api/SeenApiContext.tsx'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { createTypedLocalStorage } from '#src/utils/TypedLocalStorage.ts'

const storedWeightSchema = z.object({
  v22: z.number().nullable(),
  v28: z.number().nullable(),
})

const storage = createTypedLocalStorage(
  'icd10-raf-weights',
  z.record(storedWeightSchema)
)

function loadWeightsByCode(
  codes: string[],
  queryClient: ReturnType<typeof useQueryClient>
): Map<string, Icd10RafWeight> {
  const stored = storage.get()
  const map = new Map<string, Icd10RafWeight>()
  for (const code of codes) {
    const cached = queryClient.getQueryData<Icd10RafWeight>(
      cacheKeys.icd10RafWeight(code)
    )
    if (cached) {
      map.set(code, cached)
      continue
    }
    const fromStore = stored?.[code]
    if (fromStore) {
      map.set(code, { code, v22: fromStore.v22, v28: fromStore.v28 })
    }
  }
  return map
}

function writeWeights(
  queryClient: ReturnType<typeof useQueryClient>,
  weights: Icd10RafWeight[]
): boolean {
  const stored = { ...storage.get() }
  let changed = false
  for (const weight of weights) {
    const key = cacheKeys.icd10RafWeight(weight.code)
    const current = queryClient.getQueryData<Icd10RafWeight>(key)
    if (current?.v22 === weight.v22 && current.v28 === weight.v28) {
      continue
    }
    queryClient.setQueryData(key, weight)
    stored[weight.code] = { v22: weight.v22, v28: weight.v28 }
    changed = true
  }
  if (changed) {
    storage.set(stored)
  }
  return changed
}

interface UseIcd10RafWeightsResult {
  weightsByCode: ReadonlyMap<string, Icd10RafWeight>
}

export function useIcd10RafWeights(
  codes: string[],
  options: { icd10SearchLoading: boolean }
): UseIcd10RafWeightsResult {
  const ai = useContext(SeenApiContext)?.ai
  const queryClient = useQueryClient()
  const uniqueCodes = useMemo(
    () => [
      ...new Set(
        codes.flatMap((code) => {
          const key = normalizeIcd10Key(code)
          return key ? [key] : []
        })
      ),
    ],
    [codes]
  )
  const [revision, setRevision] = useState(0)

  const weightsByCode = useMemo(
    () => loadWeightsByCode(uniqueCodes, queryClient),
    [uniqueCodes, queryClient, revision]
  )

  useEffect(() => {
    if (!ai || options.icd10SearchLoading || uniqueCodes.length === 0) {
      return
    }
    const batches = chunk(uniqueCodes, ICD10_RAF_WEIGHTS_MAX_CODES)
    const batchKeys = batches.map((batch) =>
      cacheKeys.icd10RafWeightsBatch(batch)
    )
    void Promise.all(
      batches.map((batch, index) =>
        queryClient.fetchQuery({
          queryKey: batchKeys[index],
          queryFn: async () => {
            const { weights } = await ai.icd10RafWeights({ codes: batch })
            const seen = new Set(weights.map((weight) => weight.code))
            const resolved = [...weights]
            for (const code of batch) {
              if (!seen.has(code)) {
                resolved.push({ code, v22: null, v28: null })
              }
            }
            if (writeWeights(queryClient, resolved)) {
              setRevision((current) => current + 1)
            }
            return weights
          },
        })
      )
    ).catch((error: DefaultError) => {
      // Unmounting cancels the in-flight batches below.
      if (!isCancelledError(error)) {
        throw error
      }
    })
    return () => {
      for (const batchKey of batchKeys) {
        void queryClient.cancelQueries({ queryKey: batchKey })
      }
    }
  }, [ai, options.icd10SearchLoading, queryClient, uniqueCodes])

  return { weightsByCode }
}
